import { formatMoney } from "@/lib/money/money";
import { isRecord, isStringOrNull } from "@/lib/ui/validatedJson";

/**
 * The Table display board's wire shape (`TableDisplayBoardOut`) and the rules the board shares with
 * its tests. The board is a presentation of the public browse, so each item is the same
 * `PublicStockItemOut` the phone browse reads: no cost, and a null price when prices are hidden.
 */
export type TableDisplayItem = {
  public_id: string;
  product_name: string;
  qty: number;
  condition: string | null;
  is_graded: boolean;
  grading_company?: string | null;
  grade?: string | null;
  image_url?: string | null;
  set_name?: string | null;
  number?: string | null;
  variant_label?: string | null;
  price?: string | null;
};

export type TableDisplayBuying = { name: string; set_name?: string | null; cash?: string | null; };

export type TableDisplayBoard = {
  state: "live" | "paused" | "closed";
  shop_name: string;
  // Only a live board carries them: the Event's public id is in the browse URL, and a stopped
  // show shows the shop only.
  event_name?: string | null;
  browse_url?: string | null;
  shop_url?: string | null;
  price_visibility: "ask" | "market" | "none";
  resume_in_seconds?: number | null;
  featured: TableDisplayItem[];
  just_in: TableDisplayItem[];
  buying: TableDisplayBuying[];
};

const isItem = (value: unknown): value is TableDisplayItem =>
  isRecord(value) && typeof value.public_id === "string" && typeof value.product_name === "string"
  && typeof value.qty === "number";

const isBuying = (value: unknown): value is TableDisplayBuying =>
  isRecord(value) && typeof value.name === "string";

export function isTableDisplayBoard(value: unknown): value is TableDisplayBoard {
  return isRecord(value)
    && (value.state === "live" || value.state === "paused" || value.state === "closed")
    && typeof value.shop_name === "string"
    && isStringOrNull(value.event_name ?? null) && isStringOrNull(value.browse_url ?? null)
    && (value.state !== "live" || typeof value.browse_url === "string")
    && isStringOrNull(value.shop_url ?? null)
    && (value.price_visibility === "ask" || value.price_visibility === "market"
      || value.price_visibility === "none")
    && Array.isArray(value.featured) && value.featured.every(isItem)
    && Array.isArray(value.just_in) && value.just_in.every(isItem)
    && Array.isArray(value.buying) && value.buying.every(isBuying);
}

/** How often the board asks for a fresh state. Pause must show within 5 s, so this stays under it. */
export const POLL_MS = 4_000;
/** How long one featured card stays up before the next one. */
export const ROTATE_MS = 8_000;
/** No good poll for this long reads as Reconnecting, so a stale price is never shown as live. */
export const STALE_MS = 10_000;

/** The unambiguous alphabet the backend mints codes from (no I, L, O, 0 or 1). */
const CODE_SHAPE = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{8}$/;

/** Returns the canonical code for what a person typed, or null when it cannot be a code. */
export function normalizeDisplayCode(raw: string): string | null {
  const code = raw.replace(/[\s-]/g, "").toUpperCase();
  return CODE_SHAPE.test(code) ? code : null;
}

/** Groups a code for reading aloud and typing: `K7QM4TXP` becomes `K7QM-4TXP`. */
export const groupedCode = (code: string): string => `${code.slice(0, 4)}-${code.slice(4)}`;

/**
 * The board lays out for a wide screen (16:9, an external display) or a tall one (4:3, an iPad
 * mirrored and letterboxed). Everything scales with the viewport height, so the split is the one
 * place the aspect ratio matters.
 */
export type BoardLayout = "wide" | "tall";
export const boardLayout = (width: number, height: number): BoardLayout =>
  width / Math.max(height, 1) < 1.5 ? "tall" : "wide";

/** One design unit in pixels: the board is drawn on a 720-tall canvas and scaled to the viewport. */
export const DESIGN_HEIGHT = 720;
export const unitFor = (height: number): number => Math.max(height, 1) / DESIGN_HEIGHT;

/** "Back in N min", rounded up so a board never promises a return it will miss. */
export const backInMinutes = (seconds: number): number => Math.max(1, Math.ceil(seconds / 60));

/** The condition and, for a slab, the grader and grade, in the short form a card tag shows. */
export function conditionTag(item: TableDisplayItem): string | null {
  if (item.is_graded && item.grading_company) {
    return `${item.grading_company} ${item.grade ?? ""}`.trim();
  }
  return item.condition;
}

/** The price a board draws, or null when the vendor hid it or the unit could not be priced. */
export const boardPrice = (item: { price?: string | null; }): string | null =>
  item.price ? formatMoney(item.price).replace(/\.00$/, "") : null;

/** The line under a card's name: set, then collector number. */
export const cardSubtitle = (item: TableDisplayItem): string =>
  [item.set_name, item.number].filter(Boolean).join(" · ");

/** The featured card for a rotation tick, wrapping around the list. */
export const featuredAt = <T>(items: readonly T[], tick: number): T | null =>
  items.length === 0 ? null : items[((tick % items.length) + items.length) % items.length];
